import { useGlobalPopoversContext, useSelectedStore } from 'contexts';
import type { VariantStockResponse } from 'models/api';
import type { VariantId } from 'models/product';
import {
	getProductCardStoreStockStatus,
	getProductCardWebStockStatus,
} from 'utils/business-logic/stock';
import { cnm } from 'utils/classNames';
import { useI18n } from 'utils/i18n';

import StockBalanceConcise from './StockBalanceConcise';
import StoreStockBalanceConcise from './StoreStockBalanceConcise';
import WebStockBalanceConcise from './WebStockBalanceConcise';

export interface StoreStock {
	inStock: boolean;
	isSellableInStore: boolean;
	isSellableOnWeb: boolean;
	name: string | undefined;
	productInStockAtStores: number;
}

export interface WebStock {
	isSellable: boolean;
	isSellableInStore: boolean;
	isSellableOnWeb: boolean;
	productReStockWeb: boolean;
}

export interface StockInfo {
	storeStock: StoreStock | undefined;
	webStock: WebStock;
}

interface Props {
	className?: string;
	stock: VariantStockResponse | undefined;
	stockInfo: StockInfo | undefined;
	storesEnabled: boolean;
	variantId: VariantId;
}

function LegacyStockInfo({
	buttonText,
	onOpenStoreSelector,
	stockInfo,
	storesEnabled,
}: {
	buttonText: string;
	onOpenStoreSelector: () => void;
	stockInfo: StockInfo | undefined;
	storesEnabled: boolean;
}) {
	if (!stockInfo) {
		return null;
	}
	return (
		<>
			<WebStockBalanceConcise
				status={getProductCardWebStockStatus(stockInfo?.webStock)}
			/>
			{storesEnabled && stockInfo?.storeStock && (
				<StoreStockBalanceConcise
					storeName={stockInfo.storeStock.name || ''}
					productInStockAtStores={stockInfo.storeStock.productInStockAtStores}
					onOpenStoreSelector={onOpenStoreSelector}
					buttonText={buttonText}
					status={getProductCardStoreStockStatus({
						storeSelected: Boolean(stockInfo.storeStock.name),
						...stockInfo.storeStock,
					})}
				/>
			)}
		</>
	);
}

LegacyStockInfo.displayName = 'LegacyStockInfo';

function Stock({
	buttonText,
	onOpenStoreSelector,
	stock,
	storesEnabled,
}: {
	buttonText: string | undefined;
	onOpenStoreSelector: (() => void) | undefined;
	stock: VariantStockResponse | undefined;
	storesEnabled: boolean;
}) {
	if (!stock) {
		return null;
	}
	return (
		<>
			<StockBalanceConcise
				inStock={stock.webStock.inStock}
				text={stock.webStock.text.specific}
			/>
			{storesEnabled && (
				<StockBalanceConcise
					inStock={stock.storeStock.inStock}
					text={stock.storeStock.text.specific}
					onStatusClick={onOpenStoreSelector}
					buttonText={buttonText}
				/>
			)}
		</>
	);
}

Stock.displayName = 'Stock';

export default function ProductCardStockInfo({
	className,
	stock,
	stockInfo,
	storesEnabled,
	variantId,
}: Props) {
	const { t } = useI18n();
	const { selectedStore } = useSelectedStore();
	const { openStoreSelector } = useGlobalPopoversContext();
	const hasSelectedStore = Boolean(selectedStore);

	if (!stockInfo && !stock) {
		return null;
	}

	return (
		<div className={cnm('flex flex-col gap-1', className)}>
			<LegacyStockInfo
				stockInfo={stockInfo}
				storesEnabled={storesEnabled}
				onOpenStoreSelector={() => {
					openStoreSelector(variantId);
				}}
				buttonText={t('select_store_button_text')}
			/>
			<Stock
				stock={stock}
				storesEnabled={storesEnabled}
				onOpenStoreSelector={
					hasSelectedStore
						? undefined
						: () => {
								openStoreSelector(variantId);
							}
				}
				buttonText={
					hasSelectedStore ? undefined : t('select_store_button_text')
				}
			/>
		</div>
	);
}
ProductCardStockInfo.displayName = 'ProductCard_ProductCardStockInfo';
